/**
 * Préremplissage des formulaires de rubriques issues de BlueNote Pocket.
 * Priorité : réponses enregistrées par l'utilisateur > valeurs Pocket > valeurs par défaut du formulaire.
 * Les personnes et intervenants sont stockés par identifiant d'entité et retraduits en libellés
 * courants à chaque ouverture (un renommage dans l'encodage reste donc sans effet).
 */

export const POCKET_ENTITY_DATA_KEYS = {
  personnePhysique: '_personnesData',
  intervenant: '_intervenantsData'
};

// Valeurs injectées par le formulaire lui-même (données du PV) : jamais enregistrées comme réponses
const REPORT_BASE_KEYS = new Set(['reportType', 'indice', 'number', 'year', 'unit']);

const isDerivedKey = (key) => key.startsWith('_') || key.endsWith('_data') || key.endsWith('_gender');

const parseTime = (value) => {
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const ms = Date.parse(value);
    return Number.isNaN(ms) ? null : ms;
  }
  return null;
};

/**
 * Point de départ du formulaire d'une rubrique Pocket.
 * @returns {{ source: 'server'|'draft'|'prefill', values: object, insertValues: object|null,
 *             modifiedFields: string[], entitySelections: object }}
 */
export const choosePocketStart = (pocket, draft) => {
  const server = pocket && pocket.workingAnswers && typeof pocket.workingAnswers === 'object' ? pocket.workingAnswers : null;
  const hasDraft = Boolean(draft && draft.formValues);
  if (server && hasDraft) {
    const serverAt = parseTime(server.savedAt);
    const draftAt = parseTime(draft.savedAt);
    if (draftAt !== null && (serverAt === null || draftAt > serverAt)) return { source: 'draft' };
  }
  if (server) {
    return {
      source: 'server',
      values: server.values || {},
      insertValues: server.insertValues || null,
      modifiedFields: Array.isArray(server.modifiedFields) ? server.modifiedFields : [],
      entitySelections: server.entitySelections || {}
    };
  }
  if (hasDraft) return { source: 'draft' };
  const prefill = (pocket && pocket.prefill) || {};
  const entitySelections = {};
  Object.entries(prefill.entities || {}).forEach(([field, entity]) => {
    if (entity && Array.isArray(entity.ids) && POCKET_ENTITY_DATA_KEYS[entity.type]) {
      entitySelections[field] = { dataKey: POCKET_ENTITY_DATA_KEYS[entity.type], ids: entity.ids };
    }
  });
  const values = prefill.values || {};
  return {
    source: 'prefill',
    values,
    insertValues: null,
    modifiedFields: Object.keys(values).concat(Object.keys(entitySelections)),
    entitySelections
  };
};

/** Fusionne des valeurs dans les valeurs initiales, uniquement pour les champs réellement présents dans le formulaire. */
export const mergeKnownValues = (config, initialValues, values) => {
  const names = new Set((config || []).map((field) => field.name));
  const merged = { ...initialValues };
  Object.entries(values || {}).forEach(([key, value]) => {
    if (names.has(key)) merged[key] = value;
  });
  return merged;
};

const findLabelForId = (entityMap, id) => {
  const entry = Object.entries(entityMap || {}).find(([, option]) => option && option.data && String(option.data._id) === String(id));
  return entry ? entry[0] : null;
};

/**
 * Traduit les identifiants d'entités en libellés courants des listes du formulaire.
 * @returns {{ values: object, missing: string[] }} missing : champs dont une entité n'existe plus
 */
export const resolveEntitySelections = (fields, entityMaps, selections, currentValues) => {
  const values = { ...currentValues };
  const missing = [];
  const byName = new Map((fields || []).map((field) => [field.name, field]));
  Object.entries(selections || {}).forEach(([fieldName, selection]) => {
    const field = byName.get(fieldName);
    const entityMap = selection && entityMaps ? entityMaps[selection.dataKey] : null;
    if (!field || !entityMap || !Array.isArray(selection.ids)) return;
    const labels = selection.ids.map((id) => findLabelForId(entityMap, id));
    if (labels.some((label) => label === null)) missing.push(fieldName);
    const found = labels.filter((label) => label !== null);
    if (field.multiple) {
      values[fieldName] = Array.from(new Set(found));
      return;
    }
    const label = found.length > 0 ? found[0] : (typeof currentValues[fieldName] === 'string' && entityMap[currentValues[fieldName]] ? currentValues[fieldName] : '');
    values[fieldName] = label;
    const entry = label ? entityMap[label] : null;
    if (entry) {
      values[`${fieldName}_data`] = entry.data;
      if (selection.dataKey === '_personnesData') values[`${fieldName}_gender`] = entry.gender || '';
    } else {
      delete values[`${fieldName}_data`];
      delete values[`${fieldName}_gender`];
    }
  });
  return { values, missing };
};

/** Valeur reprise de Pocket pour chaque champ du formulaire, personnes et intervenants traduits en libellés courants. */
export const pocketExpectedValues = (fields, entityMaps, prefill) => {
  if (!prefill) return {};
  const start = choosePocketStart({ prefill }, null);
  const names = new Set((fields || []).map((field) => field.name));
  const expected = {};
  Object.entries(start.values).forEach(([name, value]) => {
    if (names.has(name)) expected[name] = value;
  });
  const { values } = resolveEntitySelections(fields, entityMaps, start.entitySelections, {});
  Object.keys(start.entitySelections).forEach((name) => {
    const value = values[name];
    if (names.has(name) && (Array.isArray(value) ? value.length > 0 : Boolean(value))) expected[name] = value;
  });
  return expected;
};

const comparable = (field, value) => {
  if (field.type === 'editor' && typeof value === 'string') {
    return value.replace(/<[^>]+>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim();
  }
  if (field.type === 'select' && Array.isArray(value)) return JSON.stringify([...value].sort());
  return JSON.stringify(value === undefined ? null : value);
};

/** Champs dont la valeur est encore celle reprise de Pocket : à vérifier, pas à remplir. */
export const pocketPrefilledFields = (fields, expected, values) => {
  const byName = new Map((fields || []).map((field) => [field.name, field]));
  const prefilled = new Set();
  Object.entries(expected || {}).forEach(([name, value]) => {
    const field = byName.get(name);
    if (field && comparable(field, (values || {})[name]) === comparable(field, value)) prefilled.add(name);
  });
  return prefilled;
};

/** Sélections d'entités courantes, exprimées en identifiants (pour l'enregistrement serveur). */
export const collectEntitySelections = (fields, entityMaps, values, getDataKey) => {
  const selections = {};
  (fields || []).forEach((field) => {
    const dataKey = getDataKey(field);
    const entityMap = dataKey && entityMaps ? entityMaps[dataKey] : null;
    if (!entityMap) return;
    const raw = values[field.name];
    const labels = Array.isArray(raw) ? raw : (typeof raw === 'string' && raw ? [raw] : []);
    const ids = labels
      .map((label) => entityMap[label] && entityMap[label].data && entityMap[label].data._id)
      .filter((id) => typeof id === 'string' && id.length > 0);
    if (labels.length > 0 && ids.length === labels.length) selections[field.name] = { dataKey, ids };
  });
  return selections;
};

/** Réponses de travail envoyées au serveur. */
export const buildWorkingAnswers = (values, insertValues, modifiedFields, entitySelections) => {
  const cleanValues = {};
  Object.entries(values || {}).forEach(([key, value]) => {
    if (!isDerivedKey(key) && !REPORT_BASE_KEYS.has(key) && value !== undefined) cleanValues[key] = value;
  });
  return {
    values: cleanValues,
    insertValues: insertValues || {},
    modifiedFields: Array.from(modifiedFields || []),
    entitySelections: entitySelections || {}
  };
};
